import { useState, useMemo, useCallback, useRef } from 'react';
import { convertN8nToFlow, createNewN8nNode, getLayoutedElements } from './editorUtils';

const MAX_UNDO = 50;

const useEditorState = (initialPlaybook, nodeTypeRegistry) => {
  const [draftNodes, setDraftNodes] = useState(
    () => initialPlaybook?.nodes ? JSON.parse(JSON.stringify(initialPlaybook.nodes)) : []
  );
  const [draftConnections, setDraftConnections] = useState(
    () => initialPlaybook?.connections ? JSON.parse(JSON.stringify(initialPlaybook.connections)) : {}
  );
  const [isDirty, setIsDirty] = useState(false);
  const undoStack = useRef([]);
  const redoStack = useRef([]);
  const parameterUndoNodeRef = useRef(null);
  const [undoCount, setUndoCount] = useState(0);

  // Use refs to access current state in callbacks without adding them as deps
  const draftNodesRef = useRef(draftNodes);
  draftNodesRef.current = draftNodes;
  const draftConnectionsRef = useRef(draftConnections);
  draftConnectionsRef.current = draftConnections;
  const nodeTypeRegistryRef = useRef(nodeTypeRegistry);
  nodeTypeRegistryRef.current = nodeTypeRegistry;

  // Derive React Flow state
  const { nodes: rfNodes, edges: rfEdges } = useMemo(() => {
    return convertN8nToFlow(draftNodes, draftConnections);
  }, [draftNodes, draftConnections]);

  const pushUndo = useCallback(() => {
    parameterUndoNodeRef.current = null;
    undoStack.current.push({
      nodes: JSON.parse(JSON.stringify(draftNodesRef.current)),
      connections: JSON.parse(JSON.stringify(draftConnectionsRef.current)),
    });
    if (undoStack.current.length > MAX_UNDO) undoStack.current.shift();
    redoStack.current = [];
    setUndoCount((c) => c + 1);
  }, []);

  const pushParameterUndo = useCallback((nodeId) => {
    if (parameterUndoNodeRef.current === nodeId) return;
    parameterUndoNodeRef.current = nodeId;
    undoStack.current.push({
      nodes: JSON.parse(JSON.stringify(draftNodesRef.current)),
      connections: JSON.parse(JSON.stringify(draftConnectionsRef.current)),
    });
    if (undoStack.current.length > MAX_UNDO) undoStack.current.shift();
    redoStack.current = [];
    setUndoCount((c) => c + 1);
  }, []);

  const undo = useCallback(() => {
    if (undoStack.current.length === 0) return;
    parameterUndoNodeRef.current = null;
    redoStack.current.push({
      nodes: JSON.parse(JSON.stringify(draftNodesRef.current)),
      connections: JSON.parse(JSON.stringify(draftConnectionsRef.current)),
    });
    const prev = undoStack.current.pop();
    setDraftNodes(prev.nodes);
    setDraftConnections(prev.connections);
    setUndoCount((c) => c + 1);
  }, []);

  const redo = useCallback(() => {
    if (redoStack.current.length === 0) return;
    parameterUndoNodeRef.current = null;
    undoStack.current.push({
      nodes: JSON.parse(JSON.stringify(draftNodesRef.current)),
      connections: JSON.parse(JSON.stringify(draftConnectionsRef.current)),
    });
    const next = redoStack.current.pop();
    setDraftNodes(next.nodes);
    setDraftConnections(next.connections);
    setUndoCount((c) => c + 1);
  }, []);

  const addNode = useCallback((typeKey, position) => {
    pushUndo();
    const existingNames = draftNodesRef.current.map((n) => n.name);
    const node = createNewN8nNode(typeKey, existingNames, position, nodeTypeRegistryRef.current);
    setDraftNodes((prev) => [...prev, node]);
    setIsDirty(true);
    return node;
  }, [pushUndo]);

  const removeNode = useCallback((nodeId) => {
    pushUndo();
    const node = draftNodesRef.current.find((n) => n.id === nodeId);
    if (!node) return;

    setDraftNodes((prev) => prev.filter((n) => n.id !== nodeId));

    // Clean up connections referencing this node
    setDraftConnections((prev) => {
      const updated = {};
      Object.entries(prev).forEach(([sourceName, data]) => {
        if (sourceName === node.name) return;
        const newMain = (data.main || []).map((outputs) =>
          (outputs || []).filter((conn) => conn.node !== node.name)
        );
        if (newMain.some((arr) => arr.length > 0)) {
          updated[sourceName] = { main: newMain };
        }
      });
      return updated;
    });
    setIsDirty(true);
  }, [pushUndo]);

  const updateNodePosition = useCallback((nodeId, position) => {
    parameterUndoNodeRef.current = null;
    setDraftNodes((prev) =>
      prev.map((n) =>
        n.id === nodeId ? { ...n, position: [Math.round(position.x), Math.round(position.y)] } : n
      )
    );
    setIsDirty(true);
  }, []);

  const updateNodeParameters = useCallback((nodeId, parameters) => {
    pushParameterUndo(nodeId);
    setDraftNodes((prev) =>
      prev.map((n) => (n.id === nodeId ? { ...n, parameters } : n))
    );
    setIsDirty(true);
  }, [pushParameterUndo]);

  const updateNodeName = useCallback((nodeId, newName) => {
    pushUndo();
    const node = draftNodesRef.current.find((n) => n.id === nodeId);
    if (!node) return;
    const oldName = node.name;

    setDraftNodes((prev) =>
      prev.map((n) => (n.id === nodeId ? { ...n, name: newName } : n))
    );

    setDraftConnections((prev) => {
      const updated = {};
      Object.entries(prev).forEach(([sourceName, data]) => {
        const key = sourceName === oldName ? newName : sourceName;
        const newMain = (data.main || []).map((outputs) =>
          (outputs || []).map((conn) => ({
            ...conn,
            node: conn.node === oldName ? newName : conn.node,
          }))
        );
        updated[key] = { main: newMain };
      });
      return updated;
    });
    setIsDirty(true);
  }, [pushUndo]);

  const addConnection = useCallback((sourceId, targetId, sourceHandle) => {
    pushUndo();
    const sourceNode = draftNodesRef.current.find((n) => n.id === sourceId);
    const targetNode = draftNodesRef.current.find((n) => n.id === targetId);
    if (!sourceNode || !targetNode) return;

    const outputIndex = sourceHandle ? parseInt(sourceHandle.replace('source-', ''), 10) : 0;

    setDraftConnections((prev) => {
      const updated = { ...prev };
      if (!updated[sourceNode.name]) updated[sourceNode.name] = { main: [] };
      const main = [...updated[sourceNode.name].main];
      while (main.length <= outputIndex) main.push([]);
      main[outputIndex] = [...main[outputIndex], { node: targetNode.name, type: 'main', index: 0 }];
      updated[sourceNode.name] = { main };
      return updated;
    });
    setIsDirty(true);
  }, [pushUndo]);

  const removeConnection = useCallback((edgeId) => {
    pushUndo();
    const parts = edgeId.split('-');
    if (parts.length < 4) return;
    const sourceId = parts[1];
    const outputIndex = parseInt(parts[2], 10);
    const targetId = parts[3];

    const sourceNode = draftNodesRef.current.find((n) => n.id === sourceId);
    const targetNode = draftNodesRef.current.find((n) => n.id === targetId);
    if (!sourceNode || !targetNode) return;

    setDraftConnections((prev) => {
      const updated = { ...prev };
      if (!updated[sourceNode.name]?.main?.[outputIndex]) return prev;
      updated[sourceNode.name] = {
        main: updated[sourceNode.name].main.map((outputs, idx) =>
          idx === outputIndex ? outputs.filter((c) => c.node !== targetNode.name) : outputs
        ),
      };
      return updated;
    });
    setIsDirty(true);
  }, [pushUndo]);

  const autoLayout = useCallback(() => {
    pushUndo();
    const { nodes: layouted } = getLayoutedElements(
      rfNodes,
      rfEdges,
    );
    setDraftNodes((prev) =>
      prev.map((n) => {
        const rf = layouted.find((ln) => ln.id === n.id);
        if (rf) return { ...n, position: [Math.round(rf.position.x), Math.round(rf.position.y)] };
        return n;
      })
    );
    setIsDirty(true);
  }, [rfNodes, rfEdges, pushUndo]);

  const resetDirty = useCallback(() => {
    setIsDirty(false);
    parameterUndoNodeRef.current = null;
    undoStack.current = [];
    redoStack.current = [];
    setUndoCount(0);
  }, []);

  return useMemo(() => ({
    draftNodes,
    draftConnections,
    rfNodes,
    rfEdges,
    isDirty,
    canUndo: undoStack.current.length > 0,
    canRedo: redoStack.current.length > 0,
    addNode,
    removeNode,
    updateNodePosition,
    updateNodeParameters,
    updateNodeName,
    addConnection,
    removeConnection,
    undo,
    redo,
    autoLayout,
    resetDirty,
  }), [
    draftNodes, draftConnections, rfNodes, rfEdges, isDirty, undoCount,
    addNode, removeNode, updateNodePosition, updateNodeParameters,
    updateNodeName, addConnection, removeConnection, undo, redo,
    autoLayout, resetDirty,
  ]);
};

export default useEditorState;
